import { Link } from 'react-router-dom';
import React, { useState, useEffect } from 'react';

const AboutPage = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const brands = [
    {
      name: "Residency Companion",
      tagline: "Smart Matchmaker for Medical Residency",
      description: "Your strategic partner for finding the ideal medical residency programs through data-driven recommendations and intelligent matching.",
      features: [
        "Intuitive search with personalized compatibility scoring",
        "IMG-specific database with 600+ programs",
        "Step 2 CK cutoffs and visa sponsorship details",
        "Strategic application planning to save costs",
        "Global accessibility with local currency pricing"
      ],
      color: "from-blue-500 to-blue-600",
      icon: "🎯",
      primaryColor: "blue"
    },
    {
      name: "Revisable AI",
      tagline: "AI-Powered Medical Exam Preparation",
      description: "Cutting-edge learning platform for medical students preparing for NEET UG, NEET PG, USMLE, PLAB, FMGE, and other high-stakes exams.",
      features: [
        "Over 3,000 test series and Qbanks with 2,000+ PYQs",
        "20,000+ AI-generated flashcards with spaced repetition",
        "300+ rapid revision notes for accelerated learning",
        "Revisable Friends for social learning and motivation",
        "Personalized, smart, and efficient study experience"
      ],
      color: "from-teal-500 to-teal-600",
      icon: "🧠",
      primaryColor: "teal"
    }
  ];

  const values = [
    {
      title: "Innovation",
      description: "We leverage cutting-edge technology to create transformative educational experiences.",
      icon: "💡"
    },
    {
      title: "Accessibility",
      description: "Making quality medical education accessible to students worldwide, regardless of location or economic background.",
      icon: "🌍"
    },
    {
      title: "Excellence",
      description: "Delivering high-quality, data-driven solutions that help students achieve their medical career goals.",
      icon: "⭐"
    },
    {
      title: "Equity",
      description: "Bridging educational gaps and democratizing premium medical education for all aspiring medical professionals.",
      icon: "⚖️"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="/" className="flex items-center">
              <img 
                src="/images/logo.png" 
                alt="Residency Companion Logo" 
                className="w-10 h-10 mr-4 object-contain"
              />
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
                Residency Companion
              </h1>
            </a>
            <div className="flex items-center space-x-1">
              <a href="/login" className="text-gray-600 hover:text-gray-900 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 hover:bg-gray-50">
                Sign In
              </a>
              <a href="/signup" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl">
                Get Started
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-teal-50">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob-enhanced"></div>
          <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-teal-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob-enhanced animation-delay-2000"></div>
        </div>
        
        <div className={`relative z-10 text-center px-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="max-w-4xl mx-auto">
            <div className="mb-6 inline-flex items-center space-x-2 bg-white/70 backdrop-blur-sm border border-gray-200 rounded-full px-4 py-2 shadow-sm">
              <span className="w-4 h-4 text-blue-600">🏥</span>
              <span className="text-sm text-gray-700 font-medium">Transforming Medical Education Since 2022</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-thin text-gray-900 mb-6 leading-tight">
              About
              <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent font-light"> Revio Education</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto font-light leading-relaxed">
              Dedicated to transforming medical education through innovative and accessible digital solutions that empower students worldwide
            </p>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-thin text-gray-900 mb-8">
            Our
            <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent font-light"> Mission</span>
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed mb-12">
            As the parent organization, Revio empowers its brands to deliver tailored, mission-driven learning experiences that help students around the world achieve their goals. We believe in democratizing premium education by bridging the gap created by expensive coaching, making high-quality medical education accessible to every aspirant.
          </p>
          
          <div className="bg-gradient-to-r from-blue-50 to-teal-50 rounded-2xl p-8 border border-gray-100">
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Founded in 2022</h3>
            <p className="text-gray-600 text-lg">
              Revio emerged with a clear mission: to democratize premium education by bridging the gap created by expensive coaching, especially in India's Tier 2 and Tier 3 cities. Our founders combined their teaching experience and deep understanding of educational inequities to build platforms that deliver high-quality, affordable exam preparation to every aspirant.
            </p>
          </div>
        </div>
      </section>

      {/* Our Brands Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-thin text-gray-900 mb-6">
              Our
              <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent font-light"> Brands</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Two specialized platforms designed to support medical students at different stages of their educational journey
            </p>
          </div>

          <div className="space-y-20">
            {brands.map((brand, index) => (
              <div key={index} className={`flex flex-col lg:flex-row items-center gap-12 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                {/* Content */}
                <div className={`flex-1 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`} style={{ transitionDelay: `${index * 300}ms` }}>
                  <div className="flex items-center mb-6">
                    <div className={`w-16 h-16 bg-gradient-to-r ${brand.color} rounded-full flex items-center justify-center mr-4 shadow-lg`}>
                      <span className="text-2xl">{brand.icon}</span>
                    </div>
                    <div>
                      <h2 className="text-4xl font-semibold text-gray-900">{brand.name}</h2>
                      <p className="text-lg text-gray-500 font-medium">{brand.tagline}</p>
                    </div>
                  </div>
                  
                  <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                    {brand.description}
                  </p>

                  <div className="space-y-3">
                    {brand.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start">
                        <div className={`w-2 h-2 bg-gradient-to-r ${brand.color} rounded-full mr-3 mt-2`}></div>
                        <span className="text-gray-600">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {brand.name === "Residency Companion" && (
                    <div className="mt-6">
                      <a
                        href="/how-it-works"
                        className={`inline-flex items-center text-${brand.primaryColor}-600 hover:text-${brand.primaryColor}-700 font-medium transition-colors`}
                      >
                        Learn How It Works
                        <span className="ml-2">→</span>
                      </a>
                    </div>
                  )}
                </div>

                {/* Visual */}
                <div className={`flex-1 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`} style={{ transitionDelay: `${index * 300 + 100}ms` }}>
                  <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-12 min-h-[300px] flex items-center justify-center">
                    <div className={`text-8xl opacity-20`}>{brand.icon}</div>
                    <div className={`absolute inset-0 bg-gradient-to-r ${brand.color} opacity-5 rounded-2xl`}></div>
                    
                    {/* Brand-specific visual elements */}
                    {brand.name === "Residency Companion" && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="grid grid-cols-3 gap-2 opacity-30">
                          {[...Array(9)].map((_, i) => (
                            <div key={i} className="w-4 h-4 bg-blue-300 rounded"></div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {brand.name === "Revisable AI" && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="grid grid-cols-4 gap-1 opacity-30">
                          {[...Array(12)].map((_, i) => (
                            <div key={i} className="w-3 h-8 bg-teal-300 rounded" style={{height: `${Math.random() * 2 + 1}rem`}}></div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-thin text-gray-900 mb-6">
              Our
              <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent font-light"> Values</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide our mission to transform medical education
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className={`text-center p-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className="w-16 h-16 bg-gradient-to-r from-blue-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">{value.icon}</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-teal-600">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-thin text-white mb-6">
              Making an Impact
            </h2>
            <p className="text-xl text-blue-100 font-light">
              Empowering medical students worldwide with innovative educational solutions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">600+</div>
              <div className="text-blue-100">Residency Programs</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">25,000+</div>
              <div className="text-blue-100">Study Resources</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">Global</div>
              <div className="text-blue-100">Accessibility</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto text-center px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-thin text-gray-900 mb-6">
            Join Our Mission
          </h2>
          <p className="text-xl text-gray-600 mb-8 font-light leading-relaxed">
            Be part of the transformation in medical education. Whether you're looking for residency matching or exam preparation, we're here to support your journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/signup"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Get Started Today
            </a>
            <a
              href="/contact"
              className="border-2 border-gray-300 text-gray-700 hover:border-blue-600 hover:text-blue-600 px-8 py-4 rounded-full text-lg font-medium transition-all duration-300"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-6">
                <img 
                  src="/images/logo.png" 
                  alt="Residency Companion Logo" 
                  className="w-12 h-12 mr-4 object-contain"
                />
                <div>
                  <h4 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
                    Residency Companion
                  </h4>
                  <p className="text-sm text-gray-500 font-medium">By Revio Education</p>
                </div>
              </div>
              <p className="text-gray-600 font-light">
                Transforming medical education through innovation and accessibility.
              </p>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-4">Product</h5>
              <ul className="space-y-3 text-gray-600">
                <li><a href="/features" className="hover:text-blue-600 transition-colors">Features</a></li>
                <li><a href="/how-it-works" className="hover:text-blue-600 transition-colors">How It Works</a></li>
                <li><a href="/api" className="hover:text-blue-600 transition-colors">API</a></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-4">Support</h5>
              <ul className="space-y-3 text-gray-600">
                <li><a href="/help" className="hover:text-blue-600 transition-colors">Help Center</a></li>
                <li><a href="/contact" className="hover:text-blue-600 transition-colors">Contact Us</a></li>
                <li><Link to="/refund-policy" className="hover:text-blue-600 transition-colors">Refund Policy</Link></li>
                <li><Link to="/cancellation-policy" className="hover:text-blue-600 transition-colors">Cancellation Policy</Link></li>
                <li><a href="/privacy" className="hover:text-blue-600 transition-colors">Privacy</a></li>
                <li><a href="/terms" className="hover:text-blue-600 transition-colors">Terms</a></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-4">Company</h5>
              <ul className="space-y-3 text-gray-600">
                <li><Link to="/about" className="text-blue-600 font-medium">About</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 mt-12 pt-8 text-center">
            <p className="text-gray-500 text-sm">
              © 2025 Revio Education Private Limited. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes blob-enhanced {
          0% { transform: translate(0px, 0px) scale(1) rotate(0deg); }
          25% { transform: translate(20px, -30px) scale(1.05) rotate(5deg); }
          50% { transform: translate(-15px, 10px) scale(0.95) rotate(-3deg); }
          75% { transform: translate(25px, -20px) scale(1.08) rotate(7deg); }
          100% { transform: translate(0px, 0px) scale(1) rotate(0deg); }
        }
        .animate-blob-enhanced { animation: blob-enhanced 9s ease-in-out infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
};

export default AboutPage;